import { ReactNode, useEffect, useMemo, useRef, useState } from 'react';
import { useVirtualizer } from '@tanstack/react-virtual';
import * as CM from '@radix-ui/react-context-menu';
import { Trash2 } from 'lucide-react';
import type { GridColumn } from './types';
import { CellEditor } from './CellEditor';
import { compare } from './DataGrid';
import { delphiColor, formatValue, isNumericType } from '@/lib/format';
import { cn } from '@/lib/cn';
import { useGridLayout } from './layout';

/** Bellek içi satır (Delphi CachedUpdates veri kümesi kaydı) */
export interface EditRow {
  /** istemci anahtarı */
  _k: string;
  row: Record<string, any>;
  /** veritabanından gelen hali (yeni satırda null) */
  original: Record<string, any> | null;
}

export const isDirty = (r: EditRow) => !r.original || Object.keys(r.row).some((k) => String(r.row[k] ?? '') !== String(r.original?.[k] ?? ''));
let seq = 0;
export const newKey = () => `n${Date.now().toString(36)}${(seq++).toString(36)}`;
export const toEditRows = (rows: Record<string, any>[]): EditRow[] => rows.map((r) => ({ _k: newKey(), row: { ...r }, original: { ...r } }));

const ROW_H = 28;

/**
 * Önbellekli (kaydet'e kadar veritabanına yazılmayan) düzenlenebilir grid — cxGridDBTableView + CachedUpdates karşılığı.
 * Hücre değişimi onChange ile üst bileşene bildirilir; satırdan ayrılırken onRowLeave (BeforePost / HESAPLAMA) çağrılır.
 */
export function EditGrid(props: {
  columns: GridColumn[];
  rows: EditRow[];
  selected: number | null;
  onSelect: (i: number | null) => void;
  onChange?: (index: number, patch: Record<string, any>) => void;
  onRowLeave?: (index: number) => void;
  beforeEdit?: (index: number, col: GridColumn) => Promise<boolean>;
  onRowDoubleClick?: (index: number) => void;
  onDelete?: (index: number) => void;
  deleteLabel?: string;
  readOnly?: boolean;
  layoutKey?: string;
  emptyText?: string;
  toolbar?: ReactNode;
  title?: ReactNode;
  className?: string;
  style?: React.CSSProperties;
  footer?: boolean;
}) {
  const layout = useGridLayout(props.layoutKey, props.columns);
  const visible = useMemo(() => layout.columns.filter((c) => !c.hidden), [layout.columns]);
  const [edit, setEdit] = useState<{ row: number; col: string } | null>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const lastRow = useRef<number | null>(props.selected);
  const v = useVirtualizer({ count: props.rows.length, getScrollElement: () => scrollRef.current, estimateSize: () => ROW_H, overscan: 12 });

  // satır değişince önceki satır için BeforePost
  useEffect(() => {
    const prev = lastRow.current;
    if (prev != null && prev !== props.selected && prev < props.rows.length && isDirty(props.rows[prev])) props.onRowLeave?.(prev);
    lastRow.current = props.selected;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.selected]);

  const totals = useMemo(() => {
    const out: Record<string, number | null> = {};
    for (const c of visible) {
      if (!c.total) continue;
      const vals = props.rows.map((r) => Number(r.row[c.key])).filter((x) => Number.isFinite(x));
      out[c.key] = c.total === 'sum' ? vals.reduce((a, b) => a + b, 0)
        : c.total === 'avg' ? (vals.length ? vals.reduce((a, b) => a + b, 0) / vals.length : null)
          : c.total === 'min' ? (vals.length ? Math.min(...vals) : null)
            : c.total === 'max' ? (vals.length ? Math.max(...vals) : null) : props.rows.length;
    }
    return out;
  }, [props.rows, visible]);

  const editable = (c: GridColumn) => !props.readOnly && !c.readonly && c.editor !== 'readonly' && !c.noFocus;

  const startEdit = async (i: number, c: GridColumn) => {
    if (!editable(c) || !props.onChange) return;
    if (props.beforeEdit && !(await props.beforeEdit(i, c))) return;
    setEdit({ row: i, col: c.key });
  };

  const moveCell = (i: number, key: string, dir: 1 | -1) => {
    const eds = visible.filter(editable);
    const idx = eds.findIndex((c) => c.key === key);
    const next = eds[idx + dir];
    if (next) { setEdit({ row: i, col: next.key }); return; }
    const ni = i + dir;
    if (ni >= 0 && ni < props.rows.length) { props.onSelect(ni); setEdit(null); }
    else setEdit(null);
  };

  const onKeyDown = (e: React.KeyboardEvent) => {
    const i = props.selected;
    if (i == null) return;
    if (edit) {
      if (e.key === 'Escape') { setEdit(null); e.stopPropagation(); }
      if (e.key === 'Tab') { e.preventDefault(); moveCell(i, edit.col, e.shiftKey ? -1 : 1); }
      if (e.key === 'Enter') { e.preventDefault(); setTimeout(() => moveCell(i, edit.col, 1)); }
      if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
        const ni = i + (e.key === 'ArrowDown' ? 1 : -1);
        if (ni >= 0 && ni < props.rows.length) { e.preventDefault(); (document.activeElement as HTMLElement)?.blur(); setTimeout(() => { props.onSelect(ni); setEdit({ row: ni, col: edit.col }); }); }
      }
      return;
    }
    if (e.key === 'ArrowDown' && i < props.rows.length - 1) { e.preventDefault(); props.onSelect(i + 1); v.scrollToIndex(i + 1); }
    if (e.key === 'ArrowUp' && i > 0) { e.preventDefault(); props.onSelect(i - 1); v.scrollToIndex(i - 1); }
    if (e.key === 'Enter' || e.key === 'F2') {
      const first = visible.find(editable);
      if (first) { e.preventDefault(); startEdit(i, first); }
    }
    if (e.key === 'Delete' && e.altKey && props.onDelete) { e.preventDefault(); props.onDelete(i); }
  };

  const cellStyle = (c: GridColumn, row?: Record<string, any>) => {
    const st: React.CSSProperties = { width: c.width ?? 120, minWidth: c.width ?? 120, textAlign: c.align ?? (isNumericType(c.sqlType) ? 'right' : undefined) };
    if (row && c.colors) {
      const ok = !c.colors.cond || c.colors.condValue == null || compare(Number(row[c.key]), c.colors.cond, c.colors.condValue);
      if (ok) {
        if (c.colors.font) st.color = delphiColor(c.colors.font);
        if (c.colors.back) st.background = delphiColor(c.colors.back);
      }
    }
    return st;
  };
  const totalWidth = visible.reduce((s, c) => s + (c.width ?? 120), 0) + 28;

  const body = (
    <div ref={scrollRef} className="min-h-0 flex-1 overflow-auto scroll-thin outline-none" tabIndex={0} onKeyDown={onKeyDown}>
      <div style={{ width: totalWidth, minWidth: '100%' }}>
        <div className="sticky top-0 z-10 flex border-b b-border bg-[var(--grid-header)] text-[11.5px] font-semibold text-[var(--text-2)]" style={{ height: ROW_H }}>
          <div className="w-7 shrink-0 border-r b-border" />
          {visible.map((c) => (
            <div key={c.key} className="relative flex shrink-0 items-center border-r b-border px-1.5" style={{ width: c.width ?? 120, minWidth: c.width ?? 120 }}>
              <span className="truncate">{c.caption}</span>
              <span className="absolute right-0 top-0 h-full w-1.5 cursor-col-resize hover:bg-[var(--accent)]/30"
                onPointerDown={(e) => resize(e, c, layout.setWidth)} />
            </div>
          ))}
        </div>
        {props.rows.length === 0 ? (
          <div className="p-6 text-center text-[12.5px] muted">{props.emptyText ?? 'Kayıt yok'}</div>
        ) : (
          <div style={{ height: v.getTotalSize(), position: 'relative' }}>
            {v.getVirtualItems().map((vi) => {
              const i = vi.index;
              const r = props.rows[i];
              const sel = props.selected === i;
              const dirty = isDirty(r);
              return (
                <div key={r._k} className={cn('absolute left-0 flex border-b b-border text-[12.5px]', sel ? 'bg-[var(--grid-select)]' : i % 2 ? 'bg-[var(--grid-row-alt)]' : '', !sel && 'hover:bg-[var(--grid-hover)]')}
                  style={{ top: vi.start, height: ROW_H, width: '100%' }}
                  onMouseDown={() => props.selected !== i && props.onSelect(i)}
                  onDoubleClick={() => props.onRowDoubleClick?.(i)}>
                  <div className={cn('flex w-7 shrink-0 items-center justify-center border-r b-border text-[10px]', dirty ? 'text-[var(--accent)]' : 'muted')}>
                    {!r.original ? '✱' : dirty ? '●' : sel ? '▸' : ''}
                  </div>
                  {visible.map((c) => {
                    const isEd = edit?.row === i && edit.col === c.key;
                    return (
                      <div key={c.key} className={cn('flex shrink-0 items-center overflow-hidden border-r b-border', editable(c) && props.onChange ? 'cursor-text' : '')}
                        style={cellStyle(c, r.row)}
                        onClick={() => { if (sel && !isEd) startEdit(i, c); }}>
                        {isEd ? (
                          <div className="h-full w-full ring-2 ring-inset ring-[var(--accent)]">
                            <CellEditor col={c} row={r.row} autoFocus onChange={(patch) => props.onChange?.(i, patch)} />
                          </div>
                        ) : (
                          <div className={cn('w-full truncate px-1.5', isNumericType(c.sqlType) && 'num')}>{c.display ? c.display(r.row) : formatValue(r.row[c.key], c.sqlType, c.mask)}</div>
                        )}
                      </div>
                    );
                  })}
                </div>
              );
            })}
          </div>
        )}
        {props.footer !== false && Object.keys(totals).length > 0 && (
          <div className="sticky bottom-0 z-10 flex border-t b-border bg-[var(--grid-header)] text-[12px] font-semibold" style={{ height: ROW_H }}>
            <div className="w-7 shrink-0 border-r b-border" />
            {visible.map((c) => (
              <div key={c.key} className="flex shrink-0 items-center justify-end border-r b-border px-1.5 num" style={{ width: c.width ?? 120, minWidth: c.width ?? 120 }}>
                {c.key in totals && totals[c.key] != null ? formatValue(totals[c.key], 'float', c.mask ?? '#,##0.00') : ''}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );

  return (
    <div className={cn('flex min-h-0 min-w-0 flex-col panel rounded', props.className)} style={props.style}>
      {(props.toolbar || props.title) && (
        <div className="flex flex-wrap items-center gap-1.5 border-b b-border px-2 py-1.5">
          {props.title && <span className="mr-1 text-[12.5px] font-semibold">{props.title}</span>}
          {props.toolbar}
          <span className="ml-auto text-[11px] muted">{props.rows.length} satır</span>
        </div>
      )}
      {props.onDelete && !props.readOnly ? (
        <CM.Root>
          <CM.Trigger asChild>{body}</CM.Trigger>
          <CM.Portal>
            <CM.Content className="menu z-50 min-w-44 p-1">
              <CM.Item className="menu-item" disabled={props.selected == null} onSelect={() => props.selected != null && props.onDelete!(props.selected)}>
                <Trash2 size={14} /> {props.deleteLabel ?? 'Satır Sil'} <span className="kbd ml-auto">Alt+Del</span>
              </CM.Item>
            </CM.Content>
          </CM.Portal>
        </CM.Root>
      ) : body}
    </div>
  );
}

function resize(e: React.PointerEvent, c: GridColumn, setWidth: (k: string, w: number) => void) {
  e.preventDefault();
  e.stopPropagation();
  const x0 = e.clientX, w0 = c.width ?? 120;
  const move = (ev: PointerEvent) => setWidth(c.key, Math.max(40, w0 + ev.clientX - x0));
  const up = () => { window.removeEventListener('pointermove', move); window.removeEventListener('pointerup', up); };
  window.addEventListener('pointermove', move);
  window.addEventListener('pointerup', up);
}
